import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useT } from "@/lib/i18n";

const linkDefs = [
  { href: "#ueber", key: "nav.ueber", fallback: "Über uns" },
  { href: "#werkstour", key: "nav.werkstour", fallback: "Werkstour" },
  { href: "#verfahren", key: "nav.verfahren", fallback: "Verfahren" },
  { href: "#finder", key: "nav.finder", fallback: "Finder" },
  { href: "#preis", key: "nav.preis", fallback: "Preis-Rechner" },
  { href: "#ki-analyse", key: "nav.ki_analyse", fallback: "KI-Analyse" },
  { href: "#live", key: "nav.live", fallback: "Live" },
  { href: "#fabrik-karte", key: "nav.fabrik", fallback: "Fabrik" },
  { href: "#routen", key: "nav.routen", fallback: "Routen" },
  { href: "#voice", key: "nav.voice", fallback: "Voice" },
  { href: "#jobs", key: "nav.jobs", fallback: "Jobs" },
  { href: "#team", key: "nav.team", fallback: "Team" },
];

// Primary links shown horizontally on desktop (lg:+). Keeps the top bar clean.
// The remaining links stay accessible via the mobile burger menu and by scrolling.
const PRIMARY_KEYS = new Set([
  "#ueber",
  "#verfahren",
  "#preis",
  "#ki-analyse",
  "#routen",
  "#jobs",
  "#team",
]);

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const t = useT();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = linkDefs.map((l) => ({ ...l, label: t(l.key, l.fallback) }));
  const primaryLinks = links.filter((l) => PRIMARY_KEYS.has(l.href));
  const secondaryLinks = links.filter((l) => !PRIMARY_KEYS.has(l.href));
  const ctaLabel = t("nav.cta", "Offerte einholen");
  const menuOpenLabel = t("common.open", "Öffnen");
  const menuCloseLabel = t("common.close", "Schliessen");

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/65 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-2.5 group flex-none">
          <div className="w-9 h-9 border border-white/30 flex items-center justify-center font-display font-semibold text-[13px] tracking-tighter">
            FK
          </div>
          <span className="font-display text-[15px] font-semibold tracking-tight text-white whitespace-nowrap">
            Fritz Kamm AG
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {primaryLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="text-[13px] font-medium text-zinc-300 hover:text-white transition-colors duration-300 link-underline whitespace-nowrap"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2 xl:gap-3">
          <LanguageSwitcher className="mr-1 xl:mr-2" />
          <a
            href="#kontakt"
            data-testid="nav-cta-offerte"
            className="group inline-flex items-center gap-2 bg-white text-black px-4 xl:px-5 py-2.5 text-[13px] font-semibold tracking-tight hover:bg-zinc-200 transition-colors duration-300 whitespace-nowrap"
          >
            {ctaLabel}
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <button
          aria-label={open ? menuCloseLabel : menuOpenLabel}
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden p-2 -mr-2 text-white"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Secondary row — features / additional sections, always visible on desktop */}
      <div
        data-testid="nav-secondary-row"
        className="hidden lg:block border-t border-white/5 bg-black/25 backdrop-blur-xl"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-11 flex items-center justify-center gap-6 xl:gap-10 overflow-x-auto">
          {secondaryLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-sub-link-${l.href.replace("#", "")}`}
              className="text-[11.5px] uppercase tracking-[0.16em] font-medium text-zinc-500 hover:text-white transition-colors duration-300 whitespace-nowrap"
            >
              {l.label}
            </a>
          ))}
        </div>
      </div>

      <div
        className={`lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 transition-[max-height] duration-500 ${
          open ? "max-h-[85vh] overflow-y-auto" : "max-h-0 overflow-hidden"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              data-testid={`nav-mobile-link-${l.href.replace("#", "")}`}
              className="text-base font-medium text-zinc-200 hover:text-white"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#kontakt"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-cta"
            className="mt-2 inline-flex items-center justify-center gap-2 bg-white text-black px-5 py-3 text-sm font-semibold"
          >
            {ctaLabel} <ArrowUpRight className="w-4 h-4" />
          </a>
          <div className="pt-3 border-t border-white/10 flex justify-center">
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navigation;
